import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router';
import { Loader2, AlertTriangle, UserPlus } from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  Input,
  FormField,
  NotificationCard,
  useToast,
} from '@sighthost/ui';
import {
  salesService,
  type LeadSource,
  type SalesCompany,
} from '@/domains/sales/services/salesService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';

/**
 * SalesAddLeadPage — single-lead form, sibling of SalesImportPage (CSV batch).
 *
 * Role gating mirrors the lead-create EF:
 *  - sales_agent: lead_source restricted to {walk_in, inbound_call, referral};
 *    company auto-resolved from caller's staff_profile.
 *  - sales_manager: any lead_source; company auto-resolved.
 *  - admin: any lead_source; MUST pick originating_company_id from dropdown.
 *
 * POPIA certification checkbox is REQUIRED — SP-17 same as batch-import.
 */

const ALL_SOURCES: { value: LeadSource; label: string }[] = [
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'inbound_call', label: 'Inbound call' },
  { value: 'referral', label: 'Referral' },
  { value: 'web_form', label: 'Web form' },
  { value: 'cold_list', label: 'Cold list' },
  { value: 'partner', label: 'Partner channel' },
  { value: 'csv_import', label: 'CSV import (manual)' },
];

const AGENT_ALLOWED_SOURCES = new Set<LeadSource>(['walk_in', 'inbound_call', 'referral']);

const CONSENT_OPTIONS: { value: string; label: string; hint: string }[] = [
  { value: 'opt_in_form', label: 'Opt-in form', hint: 'Customer ticked a consent box on a form' },
  { value: 'walk_in', label: 'Walk-in (verbal)', hint: 'In-person verbal consent given today' },
  { value: 'referral', label: 'Referred by existing customer', hint: 'Existing customer named them' },
  { value: 'existing_customer', label: 'Existing customer', hint: 'Already on our books in another capacity' },
  { value: 'legitimate_interest', label: 'Legitimate interest', hint: 'Business contact, documented basis' },
];

export default function SalesAddLeadPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const role = useStaffRole();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [leadSource, setLeadSource] = useState<LeadSource>('walk_in');
  const [consentBasis, setConsentBasis] = useState<string>('walk_in');
  const [consentSource, setConsentSource] = useState('');
  const [consentAt, setConsentAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [initialNote, setInitialNote] = useState('');
  const [certified, setCertified] = useState(false);
  const [originatingCompanyId, setOriginatingCompanyId] = useState('');

  const [companies, setCompanies] = useState<SalesCompany[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin needs to pick a sales_company; load active ones once.
  useEffect(() => {
    if (!role.isAdmin) return;
    let cancelled = false;
    setCompaniesLoading(true);
    salesService
      .listCompanies()
      .then((rows) => {
        if (cancelled) return;
        const active = rows.filter((r) => r.active);
        setCompanies(active);
        if (active.length > 0 && !originatingCompanyId) {
          setOriginatingCompanyId(active[0]!.id);
        }
        setCompaniesLoading(false);
      })
      .catch(() => {
        if (!cancelled) setCompaniesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [role.isAdmin, originatingCompanyId]);

  // Restrict source dropdown for agents
  const sourceOptions = useMemo(() => {
    if (role.staffRole === 'sales_agent') {
      return ALL_SOURCES.filter((o) => AGENT_ALLOWED_SOURCES.has(o.value));
    }
    return ALL_SOURCES;
  }, [role.staffRole]);

  // Validation
  const validationError = useMemo(() => {
    if (!fullName.trim()) return 'Full name is required.';
    if (!email.trim() && !phone.trim()) return 'Provide at least one of email or phone.';
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Email is malformed.';
    if (phone.trim() && !/^\+?[0-9 ]{7,20}$/.test(phone.trim())) return 'Phone is malformed (use +27… or 0… digits + spaces).';
    if (!consentSource.trim()) return 'Consent source is required (where/how consent was captured).';
    if (!consentAt) return 'Consent date is required.';
    if (role.isAdmin && !originatingCompanyId) return 'Admin must pick an originating company.';
    if (!certified) return 'You must certify the POPIA evidence statement.';
    return null;
  }, [fullName, email, phone, consentSource, consentAt, role.isAdmin, originatingCompanyId, certified]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await salesService.createLead({
        full_name: fullName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        company_name: companyName.trim() || undefined,
        lead_source: leadSource,
        consent_basis: consentBasis as
          | 'opt_in_form' | 'existing_customer' | 'referral' | 'walk_in' | 'legitimate_interest',
        consent_source: consentSource.trim(),
        consent_at: new Date(consentAt + 'T00:00:00Z').toISOString(),
        initial_note: initialNote.trim() || undefined,
        certification: true,
        originating_company_id: role.isAdmin ? originatingCompanyId : undefined,
      });

      if (result.status === 'duplicate') {
        toast({
          tone: 'warning',
          title: 'Existing customer matched',
          body: `Email or phone is already on record — opening the existing record.`,
        });
        navigate(`/sales/leads/${result.customer_id}`);
        return;
      }

      toast({
        tone: 'success',
        title: 'Lead created',
        body: `${fullName.trim()} is now on your queue.`,
      });
      navigate(`/sales/leads/${result.customer_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (role.loading) {
    return (
      <UniversalPage title="Add lead">
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      </UniversalPage>
    );
  }

  if (!role.isSalesStaff && !role.isAdmin) {
    return (
      <UniversalPage title="Add lead">
        <NotificationCard
          tone="danger"
          title="Not authorised"
          body="Only sales staff or admins can add leads."
        />
      </UniversalPage>
    );
  }

  const isAgent = role.staffRole === 'sales_agent';

  return (
    <UniversalPage
      title="Add lead"
      description="Single-lead entry — walk-ins, inbound calls, referrals. For larger batches use Import."
      breadcrumbs={[
        { label: 'Sales', href: '/sales' },
        { label: 'Leads', href: '/sales/leads' },
        { label: 'Add' },
      ]}
      actions={
        <Link to="/sales/leads">
          <Button variant="outline" size="sm" disabled={submitting}>Cancel</Button>
        </Link>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Card className="space-y-4 p-6">
          <FormField label="Full name" htmlFor="full_name" required>
            <Input
              id="full_name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={submitting}
              placeholder="Jane Mokoena"
              autoComplete="off"
              maxLength={200}
            />
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Email" htmlFor="email" helper="At least one of email or phone is required.">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                placeholder="jane@example.co.za"
                autoComplete="off"
              />
            </FormField>

            <FormField label="Phone" htmlFor="phone" helper="South African format: +27… or 0…">
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={submitting}
                placeholder="+27 82 123 4567"
                autoComplete="off"
              />
            </FormField>
          </div>

          <FormField label="Company (optional)" htmlFor="company_name">
            <Input
              id="company_name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={submitting}
              placeholder="Mokoena Logistics (Pty) Ltd"
              maxLength={200}
            />
          </FormField>

          <FormField
            label="Lead source"
            htmlFor="lead_source"
            required
            helper={
              isAgent
                ? 'Agents can self-create walk_in / inbound_call / referral only. For other sources, ask a manager to import or add.'
                : undefined
            }
          >
            <select
              id="lead_source"
              value={leadSource}
              onChange={(e) => setLeadSource(e.target.value as LeadSource)}
              disabled={submitting}
              className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {sourceOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>

          {role.isAdmin && (
            <FormField label="Originating sales company" htmlFor="originating_company_id" required>
              <select
                id="originating_company_id"
                value={originatingCompanyId}
                onChange={(e) => setOriginatingCompanyId(e.target.value)}
                disabled={submitting || companiesLoading}
                className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {companies.length === 0 && <option value="">No active companies — seed one first</option>}
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </FormField>
          )}
        </Card>

        <Card className="space-y-4 p-6">
          <header>
            <h3 className="text-sm font-semibold text-foreground">POPIA consent (required)</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              SP-17 — every lead row must record the basis on which we hold their personal information.
            </p>
          </header>

          <FormField label="Consent basis" htmlFor="consent_basis" required>
            <select
              id="consent_basis"
              value={consentBasis}
              onChange={(e) => setConsentBasis(e.target.value)}
              disabled={submitting}
              className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {CONSENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} — {o.hint}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            label="Consent source"
            htmlFor="consent_source"
            required
            helper="Where/how consent was captured — e.g. 'Walk-in to Joburg office, verbal consent', 'Filled signup form on /contact'."
          >
            <Input
              id="consent_source"
              value={consentSource}
              onChange={(e) => setConsentSource(e.target.value)}
              disabled={submitting}
              placeholder="Walk-in to office, verbal consent"
              maxLength={500}
            />
          </FormField>

          <FormField label="Consent date" htmlFor="consent_at" required>
            <Input
              id="consent_at"
              type="date"
              value={consentAt}
              onChange={(e) => setConsentAt(e.target.value)}
              disabled={submitting}
            />
          </FormField>

          <label className="flex cursor-pointer items-start gap-3 rounded border border-border bg-muted/40 p-3 text-sm">
            <input
              type="checkbox"
              checked={certified}
              onChange={(e) => setCertified(e.target.checked)}
              disabled={submitting}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              I certify that this lead consented to being contacted by us, the consent basis above is accurate,
              and I have evidence available (form, recording, written note) if requested.
            </span>
          </label>
        </Card>

        <Card className="space-y-4 p-6">
          <FormField
            label="Initial note (optional)"
            htmlFor="initial_note"
            helper="Quick context — what they're looking for, when to follow up, etc. Visible to all sales staff who can see this lead."
          >
            <textarea
              id="initial_note"
              value={initialNote}
              onChange={(e) => setInitialNote(e.target.value)}
              disabled={submitting}
              maxLength={4000}
              rows={4}
              className="w-full rounded border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              placeholder="Wants a R3990 Studio Starter, will pay cash. Followup Mon."
            />
          </FormField>
        </Card>

        {error && (
          <NotificationCard
            tone="danger"
            title="Could not create lead"
            body={
              <span className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </span>
            }
          />
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Link to="/sales/leads">
            <Button variant="outline" disabled={submitting}>Cancel</Button>
          </Link>
          <Button type="submit" disabled={submitting || !!validationError} loading={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" />
                Create lead
              </>
            )}
          </Button>
        </div>
      </form>
    </UniversalPage>
  );
}
